class TwoTierCollectionTabsManager {
  // グローバルキャッシュ
  static cache = new Map();
  static cacheExpiry = new Map();
  static CACHE_DURATION = 5 * 60 * 1000; // 5分間キャッシュ

  static initializeSection(sectionId) {
    const sectionElement = document.querySelector(`#shopify-section-${sectionId}`);
    
    if (!sectionElement) return;
    
    // 既に初期化済みの場合はスキップ
    if (sectionElement.hasAttribute('data-tabs-initialized')) {
      return;
    }
    
    // 初期化済みマークを追加
    sectionElement.setAttribute('data-tabs-initialized', 'true');
    
    class TwoTierCollectionTabs {
      constructor(element) {
        this.section = element;
        this.actualSectionId = this.section.id.replace('shopify-section-', '');
        this.sectionId = sectionId;
        this.currentPrimaryTab = null;
        this.currentSecondaryTab = null;
        this.secondaryTabUrls = [];
        this.secondaryTabNames = [];
        this.secondaryTabsData = []; // JSON形式の完全なデータ
        this.loadingTabs = new Set();
        this.init();
      }
      
      init() {
        this.primaryTabs = this.section.querySelectorAll('.tabs-nav__item');
        this.secondaryTabContainer = this.section.querySelector('.tabs-nav__item-list');
        this.secondaryTabs = this.section.querySelectorAll('.tabs-nav__item-second');
        this.productList = this.section.querySelector('.product-list');
        
        // 第2階層のTabsNavカスタムエレメントを取得
        this.secondaryTabsNav = this.section.querySelector('.tabs-nav:last-of-type');
        
        this.bindEvents();
        this.setInitialActiveTab();
        this.preloadActiveCollections(); // 初期プリロード
        
        // TabsNavの初期化を待つ
        this.initializeSecondaryTabsNav();
      }
      
      // 第2階層TabsNavの初期化とカスタマイズ
      initializeSecondaryTabsNav() {
        if (this.secondaryTabsNav) {
          // TabsNavが完全に初期化されるまで待つ
          setTimeout(() => {
            this.setupSecondaryTabsNavIntegration();
          }, 100);
        }
      }
      
      setupSecondaryTabsNavIntegration() {
        // TabsNavのpositionElementを取得（アクティブタブの下線）
        this.positionElement = this.secondaryTabsNav.querySelector('.tabs-nav__position');
        
        if (!this.positionElement) {
          // positionElementが存在しない場合は作成
          this.createPositionElement();
        }
        
        // TabsNavのイベントをリッスン
        this.secondaryTabsNav.addEventListener('tabs-nav:changed', (event) => {
          const button = event.detail.button;
          if (button.classList.contains('tabs-nav__item-second')) {
            this.handleSecondaryTabClick({ target: button });
          }
        });
      }
      
      createPositionElement() {
        if (this.positionElement) return; // 既に存在する場合はスキップ
        
        this.positionElement = document.createElement('span');
        this.positionElement.classList.add('tabs-nav__position', 'custom-position');
        
        const itemList = this.secondaryTabContainer;
        if (itemList) {
          // 親要素に相対位置を設定
          itemList.style.position = 'relative';
          itemList.appendChild(this.positionElement);
        }
      }
      
      bindEvents() {
        // 第1階層タブのイベント
        this.primaryTabs.forEach((button, index) => {
          button.addEventListener('click', (e) => {
            // console.log(`Section ${this.sectionId}: Primary tab clicked:`, index);
            this.handlePrimaryTabClick(e);
          });
          
          // ホバー時のプリロード（オプション）
          button.addEventListener('mouseenter', (e) => {
            this.preloadCollection(button.getAttribute('data-collection-url'));
          });
        });
        
        // 第2階層タブのイベント（直接的なイベントバインディング）
        this.bindSecondaryTabEvents();
        
        // ホバー時のプリロード（第2階層タブ用）
        this.section.addEventListener('mouseenter', (e) => {
          if (e.target.classList.contains('tabs-nav__item-second')) {
            const collectionUrl = e.target.getAttribute('data-collection-url');
            this.preloadCollection(collectionUrl);
          }
        }, true);
      }
      
      // 第2階層タブのイベントを直接バインド
      bindSecondaryTabEvents() {
        this.secondaryTabs.forEach((button, index) => {
          // 既存のイベントリスナーを削除
          button.removeEventListener('click', this.handleSecondaryTabClickBound);
          
          // 新しいイベントリスナーをバインド
          this.handleSecondaryTabClickBound = (e) => {
            e.preventDefault();
            e.stopPropagation();
            // console.log(`Section ${this.sectionId}: Secondary tab ${index} clicked directly`);
            this.handleSecondaryTabClick(e);
          };
          
          button.addEventListener('click', this.handleSecondaryTabClickBound);
        });
      }
      
      setInitialActiveTab() {
        const defaultTab = this.section.querySelector('.tabs-nav__item[aria-expanded="true"]');
        if (defaultTab) {
          defaultTab.classList.add('is-active');
          this.currentPrimaryTab = defaultTab;
          this.updateSecondaryTabs(defaultTab);
        }
      }
      
      async handlePrimaryTabClick(event) {
        const button = event.target;
        
        // 既に同じタブがアクティブな場合はスキップ
        if (button === this.currentPrimaryTab) {
          return;
        }
        
        this.updatePrimaryTabState(button);
        this.currentPrimaryTab = button;
        
        // 第2階層タブを更新
        this.updateSecondaryTabs(button);
        
        // 第2階層の最初のタブの商品を自動表示
        const firstSecondaryTab = this.section.querySelector('.tabs-nav__item-second[aria-expanded="true"]');
        if (firstSecondaryTab) {
          this.handleSecondaryTabClick({ target: firstSecondaryTab }, true);
        }
      }
      
      updateSecondaryTabs(primaryTab) {
        const tabsData = primaryTab.getAttribute('data-secondary-tabs');
        
        if (!tabsData) {
          console.warn(`Section ${this.sectionId}: No secondary tabs data found for primary tab`);
          return;
        }
        
        try {
          const secondaryTabsData = JSON.parse(tabsData);
          
          // console.log(`Section ${this.sectionId}: Secondary tabs data:`, secondaryTabsData);
          
          // URLと名前の配列を更新
          this.secondaryTabUrls = secondaryTabsData.map(tab => tab.url);
          this.secondaryTabNames = secondaryTabsData.map(tab => tab.name);
          this.secondaryTabsData = secondaryTabsData; // 完全なデータも保持
          
          // 第2階層タブのdata-collection-urlとテキストを更新
          this.secondaryTabs.forEach((tab, index) => {
            const tabData = secondaryTabsData[index];
            
            if (tabData && tabData.url) {
              tab.setAttribute('data-collection-url', tabData.url);
              tab.setAttribute('data-collection-handle', tabData.handle);
              tab.setAttribute('data-collection-id', tabData.id);
              tab.textContent = tabData.name || `タブ ${index + 1}`;
              tab.style.display = 'block';
              
              // 最初のタブをアクティブに設定
              if (index === 0) {
                tab.setAttribute('aria-expanded', 'true');
                tab.classList.add('is-active');
                this.currentSecondaryTab = tab;
              } else {
                tab.setAttribute('aria-expanded', 'false');
                tab.classList.remove('is-active');
              }
            } else {
              // データがない場合は非表示
              tab.style.display = 'none';
              tab.removeAttribute('data-collection-url');
              tab.removeAttribute('data-collection-handle');
              tab.removeAttribute('data-collection-id');
              tab.textContent = '';
            }
          });
          
          // TabsNavのposition要素の位置を更新
          setTimeout(() => {
            // 第2階層タブのイベントを再バインド
            this.bindSecondaryTabEvents();
          }, 100);
          
        } catch (error) {
          console.error(`Section ${this.sectionId}: Failed to parse secondary tabs data:`, error);
          // console.log('Raw data:', tabsData);
          
          // フォールバック：従来の方式を試行
          this.updateSecondaryTabsFallback(primaryTab);
        }
      }
      
      // フォールバック機能（従来の方式）
      updateSecondaryTabsFallback(primaryTab) {
        const urlListData = primaryTab.getAttribute('data-second-tab-url-list');
        
        if (!urlListData) return;
        
        console.warn(`Section ${this.sectionId}: Using fallback method for secondary tabs`);
        
        const urlAndNamePairs = urlListData
          .split(',')
          .map(item => item.trim())
          .filter(item => item.length > 0)
          .map(item => {
            const [url, name] = item.split(':');
            return {
              url: url ? url.trim() : '',
              name: name ? name.trim() : ''
            };
          });
        
        this.secondaryTabUrls = urlAndNamePairs.map(pair => pair.url);
        this.secondaryTabNames = urlAndNamePairs.map(pair => pair.name);
        
        this.secondaryTabs.forEach((tab, index) => {
          if (this.secondaryTabUrls[index]) {
            tab.setAttribute('data-collection-url', this.secondaryTabUrls[index]);
            tab.textContent = this.secondaryTabNames[index] || `タブ ${index + 1}`;
            tab.style.display = 'block';
            
            if (index === 0) {
              tab.setAttribute('aria-expanded', 'true');
              tab.classList.add('is-active');
              this.currentSecondaryTab = tab;
            } else {
              tab.setAttribute('aria-expanded', 'false');
              tab.classList.remove('is-active');
            }
          } else {
            tab.style.display = 'none';
            tab.removeAttribute('data-collection-url');
            tab.textContent = '';
          }
        });
      }
      
      async handleSecondaryTabClick(event, silent = false) {
        const button = event.target;
        const collectionUrl = button.getAttribute('data-collection-url');
        
        // if (!silent) {
        //   console.log(`Section ${this.sectionId}: Secondary tab clicked:`, button.textContent, collectionUrl);
        // }
        
        if (!collectionUrl) {
          console.error(`Section ${this.sectionId}: No collection URL found`);
          return;
        }
        
        // 既に同じタブがアクティブな場合はスキップ
        if (button === this.currentSecondaryTab && !silent) {
          // console.log(`Section ${this.sectionId}: Same tab clicked, skipping`);
          return;
        }
        
        // 即座にcurrentSecondaryTabを更新（位置調整の前に）
        this.currentSecondaryTab = button;
        
        // UI状態を即座に更新
        this.updateSecondaryTabState(button);
        
        // スライダー状態を事前にリセット（コンテンツ更新前）
        this.resetSliderPosition();
        
        // キャッシュキー
        const cacheKey = `${collectionUrl}:${this.actualSectionId}`;
        
        // キャッシュから取得を試行
        const cachedData = this.getCachedData(cacheKey);
        if (cachedData) {
          // console.log(`Section ${this.sectionId}: Using cached data for ${collectionUrl}`);
          this.updateProductList(cachedData);
          return;
        }
        
        // キャッシュにない場合は取得
        if (!silent) {
          this.showLoading();
        }
        
        try {
          const data = await this.fetchAndCacheCollection(collectionUrl, cacheKey, silent);
          if (data) {
            this.updateProductList(data);
          }
        } catch (error) {
          console.error(`Section ${this.sectionId}: コレクションの切り替えに失敗しました:`, error);
        } finally {
          if (!silent) {
            this.hideLoading();
          }
        }
      }
      
      updatePrimaryTabState(activeButton) {
        this.primaryTabs.forEach(btn => {
          btn.setAttribute('aria-expanded', 'false');
          btn.classList.remove('is-active');
        });
        
        activeButton.setAttribute('aria-expanded', 'true');
        activeButton.classList.add('is-active');
      }
      
      updateSecondaryTabState(activeButton) {
        // console.log(`Section ${this.sectionId}: Updating tab state for:`, activeButton.textContent.trim());
        
        // 全てのタブの状態をリセット
        this.secondaryTabs.forEach((btn, index) => {
          btn.setAttribute('aria-expanded', 'false');
          btn.classList.remove('is-active');
          // console.log(`Section ${this.sectionId}: Reset tab ${index}:`, btn.textContent.trim());
        });
        
        // アクティブタブを設定
        activeButton.setAttribute('aria-expanded', 'true');
        activeButton.classList.add('is-active');
        
        // console.log(`Section ${this.sectionId}: Set active tab:`, activeButton.textContent.trim());
      }
      
      // キャッシュからデータを取得
      getCachedData(cacheKey) {
        const cached = TwoTierCollectionTabsManager.cache.get(cacheKey);
        const expiry = TwoTierCollectionTabsManager.cacheExpiry.get(cacheKey);
        
        if (cached && expiry && Date.now() < expiry) {
          return cached;
        }
        
        // 期限切れの場合は削除
        if (cached) {
          TwoTierCollectionTabsManager.cache.delete(cacheKey);
          TwoTierCollectionTabsManager.cacheExpiry.delete(cacheKey);
        }
        
        return null;
      }
      
      // コレクションを取得してキャッシュに保存
      async fetchAndCacheCollection(collectionUrl, cacheKey, silent = false) {
        // リクエストURLにセクションパラメータを追加
        const url = `${collectionUrl}?sections=${this.actualSectionId}`;
        
        if (!silent) {
          // console.log(`Section ${this.sectionId}: Fetching ${url}`);
        }
        
        // 同時リクエスト防止
        if (this.loadingTabs.has(cacheKey)) {
          // console.log(`Section ${this.sectionId}: Request already in progress for ${cacheKey}`);
          return null;
        }
        
        try {
          this.loadingTabs.add(cacheKey);
          
          const response = await fetch(url);
          const data = await response.json();
          
          if (data[this.actualSectionId]) {
            // キャッシュに保存
            TwoTierCollectionTabsManager.cache.set(cacheKey, data[this.actualSectionId]);
            TwoTierCollectionTabsManager.cacheExpiry.set(
              cacheKey, 
              Date.now() + TwoTierCollectionTabsManager.CACHE_DURATION
            );
            
            // if (!silent) {
            //   console.log(`Section ${this.sectionId}: Cached data for ${cacheKey}`);
            // }
            
            return data[this.actualSectionId];
          }
          
          throw new Error(`Section data not found for ${this.actualSectionId}`);
        } finally {
          this.loadingTabs.delete(cacheKey);
        }
      }
      
      updateProductList(newSectionHtml) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newSectionHtml;
        
        const newProductListInner = tempDiv.querySelector('.product-list__inner');
        const currentProductListInner = this.section.querySelector('.product-list__inner');
        
        if (newProductListInner && currentProductListInner) {
          // スライダー位置をリセット
          this.resetSliderPosition();
          
          // スムーズなアニメーション付きで更新
          this.animateProductListUpdate(currentProductListInner, newProductListInner);
          this.updateNavigationButtons(tempDiv);
          
          // ボタンリンクも更新
          this.updateButtonLinks(tempDiv);
          
          // スライダーの再初期化
          setTimeout(() => {
            this.reinitializeSlider();
          }, 250);
        }
      }
      
      // スライダー位置をリセット
      resetSliderPosition() {
        const scroller = this.section.querySelector('.product-list__inner--scroller');
        if (scroller) {
          // スクロール位置を初期位置にリセット
          scroller.scrollTo({
            left: 0,
            behavior: 'auto'
          });
        }
        
        // prev-next-buttonsの状態をリセット
        const prevButton = this.section.querySelector('.prev-next-button--prev');
        const nextButton = this.section.querySelector('.prev-next-button--next');
        
        if (prevButton) {
          prevButton.disabled = true;
        }
        if (nextButton) {
          nextButton.disabled = false;
        }
      }
      
      // スライダーの再初期化
      reinitializeSlider() {
        const productList = this.section.querySelector('product-list');
        if (productList) {
          // カスタムエレメントの再初期化をトリガー
          const event = new CustomEvent('slider:reinit');
          productList.dispatchEvent(event);
          
          // または、カスタムエレメントのメソッドを直接呼び出し
          if (typeof productList._updateArrowsState === 'function') {
            productList._updateArrowsState();
          }
          
          // スタガー効果の再適用
          if (productList.hasAttribute('stagger-apparition')) {
            this.restaggerProducts();
          }
        }
      }
      
      // 商品のスタガー効果を再適用
      restaggerProducts() {
        const productItems = this.section.querySelectorAll('.product-item');
        productItems.forEach((item, index) => {
          item.style.animationDelay = `${index * 100}ms`;
          item.classList.remove('is-visible');
          
          setTimeout(() => {
            item.classList.add('is-visible');
          }, 50);
        });
      }
      
      // スムーズなアニメーション（改良版 - FeaturedCollectionTabsManagerと同じタイミング）
      animateProductListUpdate(currentElement, newElement) {
        // アニメーション開始前にスクロール位置をリセット
        const scroller = currentElement.closest('.scroller');
        if (scroller) {
          scroller.scrollTo({
            left: 0,
            behavior: 'auto'
          });
        }
        
        // FeaturedCollectionTabsManagerと同じアニメーション設定
        currentElement.style.transition = 'opacity 0.2s ease';
        currentElement.style.opacity = '0';
        
        setTimeout(() => {
          currentElement.innerHTML = newElement.innerHTML;
          currentElement.style.opacity = '1';
          
          setTimeout(() => {
            currentElement.style.transition = '';
          }, 400);
        }, 400); // 400msの待機時間で確実にフェードアウト完了後に更新
      }
      
      updateNavigationButtons(newSectionElement) {
        const newNavButtons = newSectionElement.querySelector('.product-list__prev-next');
        const currentNavButtons = this.section.querySelector('.product-list__prev-next');
        
        if (newNavButtons && currentNavButtons) {
          // 新しいナビゲーションボタンで置き換え
          currentNavButtons.replaceWith(newNavButtons.cloneNode(true));
          
          // 新しいボタンのイベントリスナーを再バインド
          setTimeout(() => {
            this.rebindNavigationEvents();
          }, 100);
        } else if (newNavButtons && !currentNavButtons) {
          // ナビゲーションボタンが新しく追加される場合
          const productList = this.section.querySelector('.product-list');
          if (productList) {
            productList.appendChild(newNavButtons.cloneNode(true));
            setTimeout(() => {
              this.rebindNavigationEvents();
            }, 100);
          }
        } else if (!newNavButtons && currentNavButtons) {
          // ナビゲーションボタンが削除される場合
          currentNavButtons.remove();
        }
      }
      
      // ナビゲーションボタンのイベントを再バインド
      rebindNavigationEvents() {
        const productList = this.section.querySelector('product-list');
        const prevButton = this.section.querySelector('.prev-next-button--prev');
        const nextButton = this.section.querySelector('.prev-next-button--next');
        const scroller = this.section.querySelector('.product-list__inner--scroller');
        
        if (productList && prevButton && nextButton && scroller) {
          // カスタムエレメントの再初期化
          if (typeof productList.connectedCallback === 'function') {
            productList.connectedCallback();
          }
          
          // または手動でイベントを再バインド
          this.bindSliderEvents(prevButton, nextButton, scroller);
        }
      }
      
      // スライダーイベントの手動バインド
      bindSliderEvents(prevButton, nextButton, scroller) {
        const productItems = this.section.querySelectorAll('.product-item');
        if (productItems.length === 0) return;
        
        const itemWidth = productItems[0].offsetWidth;
        const gap = 24; // 商品間のギャップ
        const scrollDistance = itemWidth + gap;
        
        // 前へボタン
        prevButton.addEventListener('click', () => {
          const currentScroll = scroller.scrollLeft;
          const newScroll = Math.max(0, currentScroll - scrollDistance);
          
          scroller.scrollTo({
            left: newScroll,
            behavior: 'smooth'
          });
          
          setTimeout(() => this.updateButtonStates(scroller, prevButton, nextButton), 300);
        });
        
        // 次へボタン
        nextButton.addEventListener('click', () => {
          const currentScroll = scroller.scrollLeft;
          const maxScroll = scroller.scrollWidth - scroller.clientWidth;
          const newScroll = Math.min(maxScroll, currentScroll + scrollDistance);
          
          scroller.scrollTo({
            left: newScroll,
            behavior: 'smooth'
          });
          
          setTimeout(() => this.updateButtonStates(scroller, prevButton, nextButton), 300);
        });
        
        // スクロールイベント
        scroller.addEventListener('scroll', () => {
          this.updateButtonStates(scroller, prevButton, nextButton);
        });
        
        // 初期状態の設定
        this.updateButtonStates(scroller, prevButton, nextButton);
      }
      
      // ボタン状態の更新
      updateButtonStates(scroller, prevButton, nextButton) {
        const scrollLeft = scroller.scrollLeft;
        const maxScroll = scroller.scrollWidth - scroller.clientWidth;
        
        // 前へボタンの状態
        if (scrollLeft <= 5) {
          prevButton.disabled = true;
        } else {
          prevButton.disabled = false;
        }
        
        // 次へボタンの状態
        if (scrollLeft >= maxScroll - 5) {
          nextButton.disabled = true;
        } else {
          nextButton.disabled = false;
        }
      }
      
      updateButtonLinks(newSectionElement) {
        const newButtonLink = newSectionElement.querySelector('.c_featured-collections__button');
        const currentButtonLink = this.section.querySelector('.c_featured-collections__button');
        
        if (newButtonLink && currentButtonLink) {
          currentButtonLink.href = newButtonLink.href;
          currentButtonLink.textContent = newButtonLink.textContent;
        }
      }
      
      showLoading() {
        if (!this.productList) return;
        
        // 既にローディング表示がある場合は削除
        this.hideLoading();
        
        // ローディングオーバーレイを作成
        const loadingOverlay = document.createElement('div');
        loadingOverlay.classList.add('loading-overlay');
        
        // サークルアニメーション要素を作成
        const circle = document.createElement('div');
        circle.classList.add('loading-circle');
        
        // オーバーレイにサークルのみを追加
        loadingOverlay.appendChild(circle);
        
        // productListに相対位置を設定（オーバーレイのため）
        const originalPosition = this.productList.style.position;
        if (!originalPosition || originalPosition === 'static') {
          this.productList.style.position = 'relative';
        }
        
        this.productList.appendChild(loadingOverlay);
      }
      
      hideLoading() {
        if (!this.productList) return;
        
        const loadingOverlay = this.productList.querySelector('.loading-overlay');
        if (loadingOverlay) {
          loadingOverlay.remove();
        }
      }
      
      // 現在の状態を取得（拡張版）
      getCurrentState() {
        return {
          primaryTab: this.currentPrimaryTab ? this.currentPrimaryTab.textContent.trim() : null,
          secondaryTab: this.currentSecondaryTab ? {
            url: this.currentSecondaryTab.getAttribute('data-collection-url'),
            handle: this.currentSecondaryTab.getAttribute('data-collection-handle'),
            id: this.currentSecondaryTab.getAttribute('data-collection-id'),
            name: this.currentSecondaryTab.textContent.trim()
          } : null,
          secondaryTabUrls: this.secondaryTabUrls,
          secondaryTabNames: this.secondaryTabNames,
          secondaryTabsData: this.secondaryTabsData
        };
      }
      
      // 特定のコレクションIDでタブを選択
      selectTabByCollectionId(collectionId) {
        if (!this.secondaryTabsData) return false;
        
        const targetIndex = this.secondaryTabsData.findIndex(tab => tab.id === collectionId);
        if (targetIndex >= 0 && this.secondaryTabs[targetIndex]) {
          this.secondaryTabs[targetIndex].click();
          return true;
        }
        return false;
      }
      
      // 特定のコレクションハンドルでタブを選択
      selectTabByCollectionHandle(handle) {
        if (!this.secondaryTabsData) return false;
        
        const targetIndex = this.secondaryTabsData.findIndex(tab => tab.handle === handle);
        if (targetIndex >= 0 && this.secondaryTabs[targetIndex]) {
          this.secondaryTabs[targetIndex].click();
          return true;
        }
        return false;
      }
      
      // アクティブなコレクションを事前読み込み
      async preloadActiveCollections() {
        const visibleTabs = Array.from(this.primaryTabs).slice(0, 3); // 最初の3つだけ
        for (const button of visibleTabs) {
          const collectionUrl = button.getAttribute('data-collection-url');
          if (collectionUrl) {
            this.preloadCollection(collectionUrl);
          }
        }
      }
      
      // プリロード（バックグラウンドで実行）
      async preloadCollection(collectionUrl) {
        if (!collectionUrl) return;
        
        const cacheKey = `${collectionUrl}:${this.actualSectionId}`;
        
        // 既にキャッシュされている場合はスキップ
        if (this.getCachedData(cacheKey)) {
          return;
        }
        
        // 既にロード中の場合はスキップ
        if (this.loadingTabs.has(cacheKey)) {
          return;
        }
        
        try {
          this.loadingTabs.add(cacheKey);
          await this.fetchAndCacheCollection(collectionUrl, cacheKey, true); // サイレント
        } catch (error) {
          console.warn(`Preload failed for ${collectionUrl}:`, error);
        } finally {
          this.loadingTabs.delete(cacheKey);
        }
      }
      
      // クリーンアップ
      destroy() {
        this.primaryTabs.forEach(button => {
          button.removeEventListener('click', this.handlePrimaryTabClick);
        });
        
        this.section.removeEventListener('click', this.handleSecondaryTabClick);
        this.section.removeEventListener('mouseenter', this.preloadCollection);
        
        // TabsNavイベントのクリーンアップ
        if (this.secondaryTabsNav) {
          this.secondaryTabsNav.removeEventListener('tabs-nav:changed', this.handleTabsNavChanged);
        }
        
        // position要素の削除
        if (this.positionElement && this.positionElement.parentNode) {
          this.positionElement.parentNode.removeChild(this.positionElement);
        }
      }
    }
    
    // インスタンス作成
    function initializeSection() {
      const instance = new TwoTierCollectionTabs(sectionElement);
      
      // プリロード実行（少し遅延させて）
      setTimeout(() => {
        instance.preloadActiveCollections();
      }, 1000);
    }
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeSection);
    } else {
      initializeSection();
    }
  }
  
  // キャッシュクリア機能（デバッグ用）
  static clearCache() {
    this.cache.clear();
    this.cacheExpiry.clear();
    // console.log('Two-tier tabs cache cleared');
  }
  
  // キャッシュ統計
  static getCacheStats() {
    const now = Date.now();
    const validEntries = Array.from(this.cacheExpiry.entries())
      .filter(([key, expiry]) => expiry > now);
    
    return {
      totalEntries: this.cache.size,
      validEntries: validEntries.length,
      expiredEntries: this.cache.size - validEntries.length
    };
  }
}